import * as React from 'react'

import DashboardContainer from 'components/smart/dashboard-container/DashboardContainer'
import {
  Box,
  Typography,
  Icon,
  InputAdornment,
} from '@mui/material'
import AttendanceCalendarView from '../tabs/AttendanceCalendarView'
import AttendanceDailyView from '../tabs/AttendanceDailyView'


import AdxInput from 'components/custom/AdxInput'
import AdxDatePickerV2 from 'components/custom/AdxDatePickerV2'
import AttendanceFilter from '../components/AttendanceFilter/AttendanceFilter'

import { format, subDays } from 'date-fns'
import AdxButton from 'components/custom/AdxButton'

import { useQuery } from 'react-query'
import { getAllCalendarAttendance, getAllDailyAttendance, exportAttendanceToCsv } from 'services/api/time-attendance'
import { useEffect, useState } from 'react'
import AdxCircularLoad from 'components/custom/AdxCircularLoad'
import AdxToggle from 'components/custom/AdxToggle'
import AdxExportButton from 'components/custom/AdxExportButton'
import { handleExcelExport } from 'services/helper/csvExport'
import SnackbarRecruitment from 'module/recruitment/reusable/SnackbarRecruitment'

const AttendanceTrackingAll = () => {
  const today = new Date()
  const oneWeekBefore = subDays(today, 6)
  const [selectedDate, setSelectedDate] = useState(today)
  const formattedSelectedDate = format(selectedDate, 'yyyy-MM-dd')

  // search related
  const [searchName, setSearchName] = useState('')
  const getSearchName = (e) => {
    setSearchName(e.target.value)
    setPage(0)
  }
  const searchBox = (
    <Box
      sx={{
        display: 'flex',
        gap: 2,
        justifyContent: 'space-between',
        width: { xs: '100%', md: 'auto' },
      }}
    >
      <AdxInput
        sx={{
          width: { xs: '100%', md: '285px' },
          '& .MuiInputBase-root': {
            outline: 'solid 1px gray-line',
          },
        }}
        value={searchName}
        onChange={getSearchName}
        startAdornment={
          <InputAdornment position="start">
            <Icon>search</Icon>
          </InputAdornment>
        }
        placeholder="Search employee name"
      />
      {/* <Box
        sx={{
          color: 'alternate.main',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <ExportIcon width={25} />
      </Box> */}
    </Box>
  )

  // pagination related
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const handleChangePage = (event, newPage) => {
    setPage(newPage)
  }
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0)
  }

  // tab related
  const [view, setView] = React.useState('calendar')
  const handleTabChange = (event, newView) => {
    if (newView !== null) {
      setView(newView)
    }
    setFilterValues(initialFilterValues)
    setRowsPerPage(10)
    setPage(0)
  }
  const tabs = [
    {
      label: 'Calendar View',
      value: 'calendar',
    },
    {
      label: 'Daily View',
      value: 'daily',
    },
  ]

  // filter drawer related
  const [drawerState, setDrawerState] = useState({ right: false })
  const initialFilterValues = {
    startDate: oneWeekBefore,
    endDate: today,
    duration: '',
    dept: 'all',
    status: 'all',
  }
  const [filterValues, setFilterValues] = useState(initialFilterValues)
  const [exportLoading, setExportLoading] = useState(false)
  const [successSnackbarOpen, setSuccessSnackbarOpen] = useState(false)
  const [errorSnackbarOpen, setErrorSnackbarOpen] = useState(false)
  const [snackbarMessage, setSnackbarMessage] = useState('')
  const formattedStartDate = format(filterValues.startDate, 'yyyy-MM-dd')
  const formattedEndDate = format(filterValues.endDate, 'yyyy-MM-dd')

  // API CALL
  const {
    isLoading,
    data: attData,
    refetch,
  } = useQuery('attendanceData', async () => {
    if (view === 'calendar') {
      const { data } = await getAllCalendarAttendance(
        formattedStartDate,
        formattedEndDate,
        page,
        rowsPerPage,
        searchName
      )
      return data
    } else {
      const { data } = await getAllDailyAttendance(
        formattedSelectedDate,
        page,
        rowsPerPage,
        searchName
      )
      return data
    }
  })

  const attendanceData = attData?.attendances
  const total = attData?.meta.total

  useEffect(() => {
    refetch()
  }, [filterValues, selectedDate, page, rowsPerPage, searchName, view, refetch])

  // Handle CSV export
  const handleExport = async (exportData) => {
    try {
      setExportLoading(true)
      
      // Add search parameter to export data
      const exportPayload = {
        ...exportData,
        search: searchName
      }
      
      const response = await exportAttendanceToCsv(exportPayload)
      const result = handleExcelExport(response, 'attendance_records.xlsx')
      
      if (result.success) {
        setSnackbarMessage(`Attendance records exported successfully as ${result.filename}`)
        setSuccessSnackbarOpen(true)
      } else {
        setSnackbarMessage('Failed to export attendance records')
        setErrorSnackbarOpen(true)
      }
    } catch (error) {
      console.error('Export error:', error)
      setSnackbarMessage('Failed to export attendance records')
      setErrorSnackbarOpen(true)
    } finally {
      setExportLoading(false)
    }
  }

  return (
    <DashboardContainer headerTitle="Attendance Tracking" blockMobile isBeta>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        <Box
          sx={{
            alignItems: 'center',
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            height: { xs: null },
          }}
        >
          <Box
            sx={{
              width: { xs: '100%', md: '30%' },
              height: '35px',
              display: { xs: 'none', md: 'flex' },
              alignItems: 'center',
              justifyContent: { xs: 'center', md: 'start' },
            }}
          >
            <Typography variant="h4">All employee</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'center', width: { xs: '100%', md: '40%' } }}>
            <AdxToggle items={tabs} view={view} handleTabChange={handleTabChange} />
          </Box>
          <Box
            sx={{
              width: { xs: '100%', md: '30%' },
              height: '100%',
              alignText: 'center',
              justifyContent: 'center',
            }}
          ></Box>
        </Box>
        <Box
          sx={{
            border: '1px solid',
            borderColor: 'gray-line.main',
            backgroundColor: 'white.main',
            borderRadius: '4.5px',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              justifyContent: { xs: 'center', md: 'space-between' },
              gap: 2,
              p: 2,
              borderBottom: '1px solid',
              borderColor: 'gray-line.main',
              width: '100%',
            }}
          >
            {view === 'calendar' && (
              <AttendanceFilter
                filterValues={filterValues}
                setFilterValues={setFilterValues}
                setDrawerState={setDrawerState}
                drawerState={drawerState}
              />
            )}

            {view === 'daily' && (
              <Box sx={{ display: 'flex', gap: 1 }}>
                <AdxDatePickerV2
                  placeholder={format(selectedDate, 'EEEE, MMM dd yyyy')}
                  selectedDate={selectedDate}
                  setSelectedDate={setSelectedDate}
                />
                <AdxButton variant={'outlined'} onClick={() => setSelectedDate(today)}>
                  Today
                </AdxButton>
              </Box>
            )}
            <Box
              sx={{
                display: 'flex',
                gap: 2,
                justifyContent: 'space-between',
                width: { xs: '100%', md: 'auto' },
                alignItems: 'center'
              }}
            >
              {searchBox}
              <AdxExportButton
                onExport={handleExport}
                loading={exportLoading}
                disabled={!attendanceData || attendanceData.length === 0}
                variant="outlined"
                size="small"
                withDepartmentFilter
              />
            </Box>
          </Box>
          {attendanceData ? (
            view === 'calendar' ? (
              <AttendanceCalendarView
                attendanceData={
                  attendanceData.attendances ? attendanceData.attendances : attendanceData
                } //prevents crashes as view is changed from Daily to Calendar
                setRowsPerPage={setRowsPerPage}
                rowsPerPage={rowsPerPage}
                setPage={setPage}
                page={page}
                handleChangePage={handleChangePage}
                handleChangeRowsPerPage={handleChangeRowsPerPage}
                count={total}
                isLoading={isLoading}
                search={searchName}
              />
            ) : (
              <AttendanceDailyView
                attendanceData={attendanceData}
                setRowsPerPage={setRowsPerPage}
                rowsPerPage={rowsPerPage}
                setPage={setPage}
                page={page}
                handleChangePage={handleChangePage}
                handleChangeRowsPerPage={handleChangeRowsPerPage}
                count={total}
                isLoading={isLoading}
                search={searchName}
              />
            )
          ) : (
            <AdxCircularLoad />
          )}
        </Box>
      </Box>
      
      {/* Snackbar notifications */}
      {successSnackbarOpen && (
        <SnackbarRecruitment
          autoHideDuration={3000}
          status="success"
          TransitionComponent="slide"
          message={snackbarMessage}
          open={successSnackbarOpen}
          handleClose={() => setSuccessSnackbarOpen(false)}
        />
      )}
      {errorSnackbarOpen && (
        <SnackbarRecruitment
          autoHideDuration={3000}
          status="error"
          TransitionComponent="slide"
          message={snackbarMessage}
          open={errorSnackbarOpen}
          handleClose={() => setErrorSnackbarOpen(false)}
        />
      )}
    </DashboardContainer>
  )
}

export default AttendanceTrackingAll
